import React, { ComponentType } from 'react';
import {
	Card,
	IconButton,
	Collapse,
	LinearProgress,
	Divider,
	CardActions,
	ListItem,
	ListItemText,
	ListItemIcon,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Chip from '@material-ui/core/Chip';
import classnames from 'classnames';
import { useToggle } from '../../utilities/hooks';
import { useCardStyles, useListItemStyles } from '../../utilities/hooks/styles';

interface Props {
	title?: string;
	subtitle?: string;
	contentCount?: number;
	alwaysExpanded?: boolean;
	startCollapsed?: boolean;
	isLoading?: boolean;
	noPadding?: boolean;
	actions?: React.ReactNode;
}
export const ToggleCard: React.FC<Props> = props => {
	const {
		title,
		subtitle,
		contentCount,
		actions,
		isLoading,
		noPadding=false,
		alwaysExpanded,
		startCollapsed,
		children
	} = props;

	const [expanded, toggleExpand] = useToggle(
		startCollapsed
			? false
			: true,
		alwaysExpanded
	);

	const cardStyles = useCardStyles();
	const itemStyles = useListItemStyles();

	const headerContent = (
		<>
			<ListItemText
				primary={
					<div className={cardStyles.centered}>
						{title}
						{
							!expanded && contentCount !== undefined &&
								<Chip label={contentCount} style={{ marginLeft: 10 }} />
						}
					</div>
				}
				secondary={subtitle}
			/>
			<ListItemIcon className={itemStyles.arrowIcon}>
				{!alwaysExpanded &&
					<IconButton className={classnames(cardStyles.expand, { [cardStyles.expandOpen]: expanded })}>
						<ExpandMoreIcon />
					</IconButton>
				}
			</ListItemIcon>
		</>
	);

	return (
		<Card className={classnames(itemStyles.margin, cardStyles.noPadding)}>
			{!alwaysExpanded ? (
				<ListItem
					button={true}
					onClick={toggleExpand}
					className={cardStyles.header}
				>
					{headerContent}
				</ListItem>
			) : (
				<ListItem
					onClick={toggleExpand}
					className={cardStyles.header}
				>
					{headerContent}
				</ListItem>
			)}
			{isLoading && <LinearProgress />}
			<Collapse
				in={expanded || alwaysExpanded}
			>
				<Divider />
				<div className={classnames(noPadding ? cardStyles.noPadding : cardStyles.padded)}>
					{children}
				</div>
				{actions && <CardActions>{actions}</CardActions>}
			</Collapse>
		</Card>
	);
};

export const wrapInCollapsibleCard = <T extends {}>(
	Component: ComponentType<T>
) => {
	type ComponentProps = T & Props;
	const WrappedCard: React.FC<ComponentProps> = props => {
		const {
			title,
			subtitle,
			contentCount,
			alwaysExpanded,
			startCollapsed,
			isLoading,
			noPadding,
			actions,
			...componentProps
		} = props;
		return (
			<ToggleCard
				title={title}
				subtitle={subtitle}
				contentCount={contentCount}
				alwaysExpanded={alwaysExpanded}
				startCollapsed={startCollapsed}
				isLoading={isLoading}
				noPadding={noPadding}
				actions={actions}
			>
				<Component {...(componentProps as T)} />
			</ToggleCard>
		);
	};

	return WrappedCard as React.ComponentType<ComponentProps>;
};
